import { useTranslation } from 'react-i18next';
import type { AnnotationDraft, DraftFailure } from '../../hooks/useAnnotationDraft';
import styles from './DraftCard.module.css';

const SAVE_FAILURE_KEYS: Record<DraftFailure, string> = {
  tooLarge: 'documents.annotations.saveTooLarge',
  refused: 'documents.annotations.saveRefused',
  failed: 'documents.annotations.saveFailed',
};

interface IDraftCardProps {
  draft: AnnotationDraft;
  onDiscard: () => void;
}

/**
 * The annotation being drawn, shown where it will land: the top of the list.
 * The note and the two ways out live here rather than in the floating toolbar —
 * that bar is what you draw WITH, this is what happens to the drawing.
 */
export function DraftCard({ draft, onDiscard }: IDraftCardProps) {
  const { t } = useTranslation();
  // What Save would store — words still in the text box count.
  const marks = draft.markup.itemsToSave;
  const total = Object.values(marks).reduce((n, list) => n + (list?.length ?? 0), 0);

  return (
    <section className={styles.card} aria-label={t('documents.annotations.draft')}>
      <div className={styles.head}>
        <span className={styles.title}>{t('documents.annotations.draft')}</span>
        <span className={styles.marks}>{t('documents.annotations.markCount', { count: total })}</span>
      </div>

      <input
        type="text"
        className={styles.note}
        value={draft.note}
        onChange={(e) => draft.setNote(e.target.value)}
        placeholder={t('documents.annotations.notePlaceholder')}
        aria-label={t('documents.annotations.note')}
        maxLength={500}
        disabled={draft.saving}
      />

      {draft.failure && (
        <p className={styles.error} role="alert">{t(SAVE_FAILURE_KEYS[draft.failure])}</p>
      )}

      <div className={styles.actions}>
        <button type="button" className={styles.discard} onClick={onDiscard} disabled={draft.saving}>
          {t('documents.annotations.discard')}
        </button>
        <button type="button" className={styles.save} onClick={draft.commit} disabled={!draft.canSave}>
          {draft.saving ? t('documents.annotations.saving') : t('documents.annotations.save')}
        </button>
      </div>
    </section>
  );
}
